/** @format */

/**
 * Browser SpeechRecognition helpers (SpeechRecognition / webkitSpeechRecognition).
 * Used by AiChatbot voice input — no server-side STT.
 */

/** @returns {typeof SpeechRecognition | null} */
export function getSpeechRecognitionConstructor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function isSpeechRecognitionSupported() {
  return Boolean(getSpeechRecognitionConstructor());
}

/**
 * @param {string} errorCode - SpeechRecognitionErrorEvent.error
 * @returns {string | null} User-facing message, or null if the error can be ignored.
 */
export function getSpeechRecognitionErrorMessage(errorCode) {
  switch (errorCode) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access was denied. Allow microphone access in your browser settings and try again.';
    case 'audio-capture':
      return 'No microphone was found. Connect a microphone and try again.';
    case 'network':
      return 'Speech recognition requires an internet connection in this browser.';
    case 'language-not-supported':
      return 'Speech recognition is not available for your language in this browser.';
    case 'aborted':
    case 'no-speech':
      return null;
    default:
      return errorCode
        ? `Speech recognition error: ${errorCode}. Try again or type your question.`
        : 'Speech recognition failed. Try again or type your question.';
  }
}

/**
 * @param {object} [options]
 * @param {boolean} [options.continuous=true]
 * @param {boolean} [options.interimResults=true]
 * @param {string} [options.lang]
 * @returns {SpeechRecognition | null}
 */
export function createSpeechRecognition(options = {}) {
  const Ctor = getSpeechRecognitionConstructor();
  if (!Ctor) return null;

  const recognition = new Ctor();
  recognition.continuous = options.continuous !== false;
  recognition.interimResults = options.interimResults !== false;
  recognition.lang = options.lang || (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
  recognition.maxAlternatives = 1;
  return recognition;
}

/**
 * Merge recognition results into a single transcript string.
 * @param {SpeechRecognitionEvent} event
 * @returns {{ finalText: string, interimText: string }}
 */
export function parseSpeechRecognitionResults(event) {
  let finalText = '';
  let interimText = '';
  for (let i = event.resultIndex; i < event.results.length; i += 1) {
    const result = event.results[i];
    const transcript = result[0]?.transcript || '';
    if (result.isFinal) {
      finalText += transcript;
    } else {
      interimText += transcript;
    }
  }
  return { finalText, interimText };
}
